import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect } from 'preact/hooks';
import { X } from 'lucide-preact';
import { TypeAheadDropdown } from './TypeAheadDropdown';
import { PersonAvatar } from './Avatar';
import { workflowSettingsApi } from '@/api/workflowSettings';
import { useToast } from '@/hooks/useToast';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
/**
 * UserPicker - A specialized type-ahead component for selecting workflow users
 *
 * Features:
 * - Type-ahead search with debouncing
 * - Shows user avatars in search results and selected chips
 * - Prevents duplicate selections
 * - Displays selected users as removable chips
 * - Integrates with workflow settings API
 *
 * @example
 * ```tsx
 * const [reviewers, setReviewers] = useState<WorkflowSettingsUserDto[]>([])
 *
 * <UserPicker
 *   label="Default Reviewers"
 *   placeholder="Search for reviewers..."
 *   workspaceId={workspaceId}
 *   clientCompanyId={clientCompanyId}
 *   userType="reviewer"
 *   selectedUsers={reviewers}
 *   onChange={setReviewers}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With React Hook Form Controller
 * <Controller
 *   name="defaultReviewerUsers"
 *   control={control}
 *   render={({ field, fieldState }) => (
 *     <UserPicker
 *       label="Default Reviewers"
 *       workspaceId={workspaceId}
 *       clientCompanyId={clientCompanyId}
 *       userType="reviewer"
 *       selectedUsers={field.value || []}
 *       onChange={field.onChange}
 *       error={fieldState.error?.message}
 *     />
 *   )}
 * />
 * ```
 */
export function UserPicker({ label, required = false, name, placeholder = 'Type to search users...', error, helperText, disabled = false, containerClassName = '', workspaceId, clientCompanyId, userType, selectedUsers, onChange }) {
    const { showError, showErrorUnless422 } = useToast();
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const debouncedSearchValue = useDebouncedValue(searchValue, 300);
    // Search for users when debounced search value changes
    useEffect(() => {
        if (debouncedSearchValue.length < 3) {
            setSearchResults([]);
            setLoading(false);
            return;
        }
        const abortController = new AbortController();
        setLoading(true);
        workflowSettingsApi
            .searchEligibleUsers(workspaceId, clientCompanyId, debouncedSearchValue, userType)
            .then((response) => {
            if (abortController.signal.aborted)
                return;
            if (response.error) {
                showErrorUnless422('Search failed', response);
                setSearchResults([]);
                return;
            }
            if (response.data) {
                const selectedIds = new Set(selectedUsers.map(u => u.Id));
                const filtered = response.data.filter(user => !selectedIds.has(user.Id));
                setSearchResults(filtered);
            }
        })
            .catch(() => {
            if (abortController.signal.aborted)
                return;
            showError('Search failed', 'Failed to search users');
            setSearchResults([]);
        })
            .finally(() => {
            if (!abortController.signal.aborted) {
                setLoading(false);
            }
        });
        return () => {
            abortController.abort();
        };
    }, [debouncedSearchValue, workspaceId, clientCompanyId, userType, selectedUsers, showError, showErrorUnless422]);
    const handleUserSelect = (option) => {
        const user = option.data;
        const newUser = {
            Id: user.Id,
            FirstName: user.FirstName,
            LastName: user.LastName,
            AvatarUrl: user.AvatarUrl
        };
        // Add to selected users
        onChange([...selectedUsers, newUser]);
        // Clear search
        setSearchValue('');
        setSearchResults([]);
    };
    const handleRemoveUser = (userId) => {
        onChange(selectedUsers.filter(u => u.Id !== userId));
    };
    const options = searchResults.map(user => ({
        key: user.Id,
        label: `${user.FirstName} ${user.LastName}`,
        data: user,
        render: () => (_jsxs("div", { className: "flex items-center gap-3", children: [_jsx(PersonAvatar, { firstName: user.FirstName, lastName: user.LastName, profilePicUrl: user.AvatarUrl, size: "sm" }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "text-sm font-medium text-slate-900 dark:text-white", children: [user.FirstName, " ", user.LastName] }), user.Email && (_jsx("div", { className: "text-xs text-slate-500 dark:text-slate-400 truncate", children: user.Email }))] })] }))
    }));
    return (_jsxs("div", { className: containerClassName, children: [_jsx(TypeAheadDropdown, { label: label, required: required, name: name, placeholder: placeholder, error: error, helperText: helperText, disabled: disabled, searchValue: searchValue, onSearchChange: setSearchValue, loading: loading, options: options, onSelect: handleUserSelect, onClear: () => setSearchValue(''), showClearButton: true, noResultsMessage: "No users found", minLengthMessage: `Type at least 3 characters to search for ${userType === 'reviewer' ? 'reviewers' : 'internal team members'}` }), selectedUsers.length > 0 && (_jsx("div", { className: "mt-3 flex flex-wrap gap-2", children: selectedUsers.map(user => (_jsxs("div", { className: "inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 rounded-full border border-slate-200 dark:border-slate-600 group hover:border-slate-300 dark:hover:border-slate-500 transition-colors", children: [_jsx(PersonAvatar, { firstName: user.FirstName, lastName: user.LastName, profilePicUrl: user.AvatarUrl, size: "sm" }), _jsxs("span", { className: "text-sm font-medium text-slate-900 dark:text-white", children: [user.FirstName, " ", user.LastName] }), !disabled && (_jsx("button", { type: "button", onClick: () => handleRemoveUser(user.Id), className: "ml-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors", "aria-label": `Remove ${user.FirstName} ${user.LastName}`, children: _jsx(X, { className: "h-4 w-4" }) }))] }, user.Id))) }))] }));
}
